import { I18LanguagesEnum } from "../enums";
import { OfferIconUi } from "../loyaltyTypes";
import { LoyaltyItemOffer } from "../LoyaltyOfferClass/LoyaltyItemOffer";
import { OfferSelectionGroup, OfferSelectionGroupKind } from "./offerSelection";
import { isFixedBundle } from "./offerShape";

/**
 * Copy for the offer page's selection groups.
 *
 * Headings come from the loyalty service (`uiConfig.selectionPage`) so counting
 * and pluralisation live in one place across clients; offers issued before that
 * shipped fall back to count-only frontend copy. The instruction line is always
 * frontend — its numbers come from config the client already has.
 */

type OfferGroupCopy = {
  title: string;
  instruction: string;
  icon?: OfferIconUi;
};

type Translate = (
  key: string,
  options?: Record<string, string | number>
) => string;

type OfferGroupCopyArgs = {
  offer: LoyaltyItemOffer;
  group: OfferSelectionGroup;
  language: I18LanguagesEnum;
  t: Translate;
  /** Locale-aware digits; defaults to `String` to keep this module pure. */
  formatNumber?: (value: number) => string;
};

function fallbackTitleKey(
  offer: LoyaltyItemOffer,
  group: OfferSelectionGroup
): string {
  if (group.kind === OfferSelectionGroupKind.Buy) {
    return "offers:offer-group-buy-title";
  }

  // A bundle has no buy side, so its group reads as a plain choice — unless
  // its items are fixed, in which case there is no choosing to describe.
  if (!offer.hasBuyRequirement()) {
    return isFixedBundle(offer.itemLevelConfig)
      ? "offers:offer-group-your-items"
      : "offers:offer-group-bundle-title";
  }

  return offer.isFreeReward()
    ? "offers:offer-group-get-free-title"
    : "offers:offer-group-get-title";
}

function serviceGroup(offer: LoyaltyItemOffer, group: OfferSelectionGroup) {
  return offer.uiConfig?.selectionPage?.groups.find(
    (entry) => entry.kind === group.kind
  );
}

export function resolveOfferGroupCopy(
  args: OfferGroupCopyArgs
): OfferGroupCopy {
  const { offer, group, language, t } = args;
  const formatNumber = args.formatNumber ?? String;

  // `count` must stay numeric for i18next to pick a plural form; `value` is
  // what gets interpolated, so locale digits survive.
  const service = serviceGroup(offer, group);
  const title =
    service?.title?.[language] ||
    t(fallbackTitleKey(offer, group), {
      count: group.requiredCount,
      value: formatNumber(group.requiredCount),
    });

  const total = formatNumber(group.entities.length);

  const instruction =
    group.maxCount > group.requiredCount
      ? t("offers:offer-group-instruction-range", {
          min: formatNumber(group.requiredCount),
          max: formatNumber(group.maxCount),
          total,
        })
      : t("offers:offer-group-instruction", {
          count: group.requiredCount,
          value: formatNumber(group.requiredCount),
          total,
        });

  return { title, instruction, icon: service?.icon };
}

/**
 * Reward-card badge, using the offer's own label so it matches the cart.
 *
 * Undefined when the offer carries no label and nothing generic fits: a
 * "Free item" fallback would misdescribe a discount or a bundle.
 */
export function resolveRewardBadgeLabel(args: {
  offer: LoyaltyItemOffer;
  language: I18LanguagesEnum;
  t: Translate;
}): string | undefined {
  const label = args.offer.getCartCardLabel(args.language);
  if (label) {
    return label;
  }

  return args.offer.isFreeReward() ? args.t("offers:free-item") : undefined;
}

/**
 * The struck-through original total the service leaves for us.
 *
 * Bundle copy arrives as `Get 2 items for SAR 10 <s>SAR {sum}</s>`: the words
 * and the currency are the service's, the figure is ours, because prices vary
 * by branch and order type while that sentence is generated once per offer.
 * We substitute digits only — the symbol, its placement and the digit script
 * are already in the string.
 */
const SUM_TOKEN = "{sum}";

/** The whole `<s>…</s>`, so an unpriced bundle loses the strike with the figure. */
const SUM_ELEMENT = /\s*<s>[^<]*\{sum\}[^<]*<\/s>/g;

export function resolveOfferSum(
  text: string,
  sum: number | undefined,
  formatNumber: (value: number) => string
): string {
  if (!text.includes(SUM_TOKEN)) {
    return text;
  }

  /*
   * Nothing to strike through reads worse than no strike at all: dropping the
   * element leaves a sentence that still stands on its own, where leaving the
   * token would show a guest a literal `{sum}`.
   */
  if (sum === undefined) {
    return text.replace(SUM_ELEMENT, "").replace(SUM_TOKEN, "").trim();
  }

  return text.split(SUM_TOKEN).join(formatNumber(sum));
}
